// Kartmodus — ÉN eier av kartets klikk om gangen.
//
// HVORFOR DENNE FILEN FINNES:
// Tegne-, måle-, rute-, akse- og pekeverktøyene registrerte hver sin
// `map.on('click')` uavhengig av hverandre. Ingen slo av de andre. Resultatet var
// ikke «litt rart», det var uleselig: én QA-runde ga et løsrevet «177.19 km» ved
// siden av måleetiketten, fordi RUTE-verktøyet fortsatt var aktivt og skrev sin
// egen popup mens operatøren trodde han målte. Samme klikk ble tolket av to
// verktøy, og kartets EGNE lag (HV-kommuneflata) svarte i tillegg med sin egen
// tooltip og satte pekeren fra trådkors til hånd — som fikk det til å se ut som
// verktøyet aldri var slått på.
//
// Dette er derfor ikke et hjelpemiddel, det er en regel: kartet har til enhver
// tid ENTEN ingen verktøyeier (og da svarer kartets egne lag) ELLER én, og da
// skal alt annet tie. En modus-eier som ikke kan navngis kan heller ikke vises
// til operatøren, og et verktøy man ikke ser at er på, er et verktøy som lyver.
//
// Ren logikk, ingen DOM og ingen MapLibre — hele arbitreringen er testbar.

export interface KartVerktoy {
  /** Stabil id (samme streng som i verktøylinja/kommandopaletten). */
  id: string;
  /** Navn til operatøren. Vises i modus-stripa: «MÅLER — Esc avbryter». */
  navn: string;
  /**
   * Slå av verktøyet. MÅ være idempotent: arbitreringen kaller den også når
   * verktøyet allerede er av, og verktøyets egen `stop()` kaller `frigi()`
   * tilbake hit.
   */
  stopp: () => void;
  /**
   * NØYTRAL eier: eier plassen, men ikke klikkene.
   *
   * HVORFOR DETTE FINNES (UX-rapporten 2026-08-14, «lag 2»): `eierKlikk()` var
   * ÉN boolsk — «noen har kartet» — og så snart et verktøy eide klikkene,
   * returnerte ALL annen seleksjon tidlig. Åtte verktøy var registrert som
   * eiere, og ingen av dem var «velg». Det fantes altså ingen tilstand der både
   * tegninger OG kartobjekter kunne merkes; eierens krav om at «alt må kunne
   * flyttes/redigeres» var ikke bare umøblert, det var utelukket av modellen.
   *
   * En nøytral eier løser det uten å svekke eksklusiviteten: den kan være aktiv
   * (og vises som standardtilstanden), men `eierKlikk()` er FALSK mens den er
   * det, så kartets egne lag svarer som før. Et ekte verktøy som tar over
   * stopper den nøytrale som alle andre.
   */
  noytral?: boolean;
}

export type ModusLytter = (aktiv: KartVerktoy | null) => void;

export class Kartmodus {
  private verktoy = new Map<string, KartVerktoy>();
  private _aktiv: string | null = null;
  private lyttere: ModusLytter[] = [];
  /** Sant mens vi bytter eier — hindrer at et `stopp()`-kall river ned den nye. */
  private bytter = false;
  /** Eieren vi faller tilbake til når ingenting annet eier kartet. */
  private _standard: string | null = null;

  registrer(v: KartVerktoy): void {
    this.verktoy.set(v.id, v);
  }

  /**
   * Sett STANDARDEIEREN — den vi faller tilbake til i stedet for «ingen».
   *
   * Må være registrert og `noytral`. En ikke-nøytral standardeier ville låst
   * kartets egne lag for godt, siden `eierKlikk()` da alltid var sann.
   */
  settStandard(id: string): void {
    const v = this.verktoy.get(id);
    if (!v) throw new Error(`Kartmodus: ukjent verktøy «${id}» — må registreres først`);
    if (!v.noytral) throw new Error(`Kartmodus: standardeieren «${id}» må være nøytral`);
    this._standard = id;
    if (this._aktiv === null) { this._aktiv = id; this.varsle(); }
  }

  /** Standardeieren, eller `null` når ingen er satt. */
  get standard(): string | null { return this._standard; }

  /** Er `id` en nøytral eier? */
  private erNoytral(id: string | null): boolean {
    return id !== null && this.verktoy.get(id)?.noytral === true;
  }

  /** Registrerte verktøy (til test og til modus-stripa). */
  get registrerte(): readonly string[] { return [...this.verktoy.keys()]; }

  get aktiv(): string | null { return this._aktiv; }
  get aktivtVerktoy(): KartVerktoy | null {
    return this._aktiv === null ? null : this.verktoy.get(this._aktiv) ?? null;
  }

  /**
   * Eier et verktøy kartklikkene nå?
   *
   * Kartets egne lag-popups (HV-kommune, feltmelding, rapport, symbol) SKAL
   * sjekke denne og returnere tidlig når den er sann. Ellers får operatøren en
   * tooltip om hvem som har ansvaret for Bærum midt i en avstandsmåling.
   *
   * En NØYTRAL eier (se `KartVerktoy.noytral`) svarer FALSK her: den er
   * standardtilstanden, ikke et verktøy, og skal ikke gjøre kartet stumt.
   */
  eierKlikk(): boolean {
    return this._aktiv !== null && !this.erNoytral(this._aktiv);
  }

  /**
   * Ta over kartet for `id`. Stopper alle andre verktøy først.
   * Returnerer id-ene som faktisk ble stoppet, slik at kalleren kan si det til
   * operatøren («Rute avbrutt») i stedet for å gjøre det stille.
   */
  taOver(id: string): string[] {
    const v = this.verktoy.get(id);
    if (!v) throw new Error(`Kartmodus: ukjent verktøy «${id}» — må registreres først`);
    if (this._aktiv === id) return [];
    const stoppet: string[] = [];
    this.bytter = true;
    try {
      for (const [annenId, annen] of this.verktoy) {
        if (annenId === id) continue;
        if (this._aktiv !== annenId) continue;
        // Standardeieren er ingen handling operatøren startet, og skal derfor
        // ikke rapporteres som «avbrutt» i statuslinja.
        if (!annen.noytral) stoppet.push(annenId);
        annen.stopp();
      }
    } finally {
      this.bytter = false;
    }
    this._aktiv = id;
    this.varsle();
    return stoppet;
  }

  /**
   * Meld at `id` er ferdig. Ignorerer meldinger fra et verktøy som ikke er eier
   * — et verktøy som stoppes under et bytte skal ikke kunne nulle den nye eieren.
   */
  frigi(id: string): void {
    if (this.bytter) return;
    if (this._aktiv !== id) return;
    // Tilbake til standardeieren, ikke til «ingen» — ellers ville «velg» vært en
    // tilstand operatøren måtte gjenopprette manuelt etter hver måling.
    this._aktiv = this._standard === id ? null : this._standard;
    this.varsle();
  }

  /**
   * Stopp det som er aktivt (Escape / «tilbake til kartet»).
   *
   * Returnerer id-en som ble stoppet, eller `null` når det ikke var noe å
   * stoppe. Standardeieren teller IKKE som noe å stoppe: Escape skal da gå
   * videre til neste ledd i gest-avtalen (opphev seleksjon, lukk panel), i
   * stedet for å bli spist av en tilstand operatøren ikke slo på.
   */
  stoppAlt(): string | null {
    const forrige = this._aktiv;
    if (forrige === null) return null;
    if (forrige === this._standard) return null;
    const v = this.verktoy.get(forrige);
    // Verktøyets egen `stop()` kaller `frigi()`, som setter tilbake og varsler.
    v?.stopp();
    // Belte og bukseseler: et verktøy som glemmer å frigi skal ikke låse kartet.
    if (this._aktiv === forrige) { this._aktiv = this._standard; this.varsle(); }
    return forrige;
  }

  onEndring(fn: ModusLytter): void { this.lyttere.push(fn); }

  private varsle() {
    const a = this.aktivtVerktoy;
    for (const fn of this.lyttere) fn(a);
  }
}
